import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../components/common/PriceDisplay";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import DateService from "../../services/dateService";
import {
    useInvestments,
    useProductStatuses,
    useProductTypes,
    useProducts,
    useProductsByIds,
} from "../../services/productsService";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../utils";
import { ProductStatusText } from "../ProductsPage/ProductStatusText";

export function ManageDocumentProductsDialog({
    open,
    onClose,
    onConfirm,
    documentData,
    initialSelectedProductIds = [],
    isLoading = false,
}) {
    const { t } = useTranslation();
    const getLocalizedProperty = useLocalizedProperty();

    const [selectedProductIds, setSelectedProductIds] = useState(initialSelectedProductIds);
    const [allProductsLocal, setAllProductsLocal] = useState([]);
    const [allProductsCountLocal, setAllProductsCountLocal] = useState(0);
    const [selectedProductsLocal, setSelectedProductsLocal] = useState([]);
    const [onlyShowSelected, setOnlyShowSelected] = useState(false);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const productSearchParams = useMemo(() => {
        const params = new URLSearchParams(pagination.searchParams);
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize]);

    const { data: allProductsData, isLoading: isProductsLoading } =
        useProducts(productSearchParams);
    useEffect(() => {
        if (allProductsData?.results) {
            setAllProductsLocal(allProductsData.results);
            setAllProductsCountLocal(allProductsData.count);
        }
    }, [allProductsData]);

    const { data: selectedProductsResponseData } = useProductsByIds(
        selectedProductIds,
        onlyShowSelected,
    );
    useEffect(() => {
        if (selectedProductsResponseData) {
            setSelectedProductsLocal(selectedProductsResponseData);
        }
    }, [selectedProductsResponseData]);

    const { data: types } = useProductTypes(new URLSearchParams(), {
        select: (data) =>
            data.map((item) => ({
                id: item.id,
                label: getLocalizedProperty(item, "name"),
            })),
    });

    const { data: productStatusesOptions } = useProductStatuses(new URLSearchParams(), {
        select: (data) =>
            data.results.map((item) => ({
                value: item.status_name_eng,
                label: t(`products.status.${item.status_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const handleProductSelection = (productId, checked) => {
        if (checked) {
            setSelectedProductIds((prev) => [...prev, productId]);
        } else {
            setSelectedProductIds((prev) => prev.filter((id) => id !== productId));
        }
    };

    const handleConfirm = () => {
        onConfirm(selectedProductIds);
    };

    const handleClose = () => {
        setSelectedProductIds(initialSelectedProductIds);
        onClose();
    };

    const handleToggleShowSelected = () => {
        setOnlyShowSelected((prev) => !prev);
    };

    useEffect(() => {
        if (open) {
            setSelectedProductIds(initialSelectedProductIds);
        }
    }, [open, initialSelectedProductIds]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
            <DialogTitle>{t("documents.ManageDocumentProductsDialog.title")}</DialogTitle>
            <DialogContent>
                <DocumentInfoCard documentData={documentData} />

                <Typography variant="h6" gutterBottom>
                    {t("documents.ManageDocumentProductsDialog.select_products")}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    {t("documents.ManageDocumentProductsDialog.description")}
                </Typography>

                <Button onClick={handleToggleShowSelected}>
                    {t(
                        onlyShowSelected
                            ? "documents.ManageDocumentProductsDialog.showAll"
                            : "documents.ManageDocumentProductsDialog.showSelected",
                    )}
                </Button>

                <LoaderWrapper showLoader={isProductsLoading && !allProductsLocal.length}>
                    <FilterTableWrapper
                        data={onlyShowSelected ? selectedProductsLocal : allProductsLocal}
                        countRecords={allProductsCountLocal}
                        localPagination={onlyShowSelected ? undefined : pagination}
                        filtersOpts={{
                            showCleanFilterIcon: !onlyShowSelected,
                            hideFiltersIconCol: onlyShowSelected,
                        }}
                        tableStyle={{
                            maxHeight: "40vh",
                            overflow: "auto",
                        }}
                        onSearchFiltersChange={pagination.handleChangeSearchParams}
                        fieldsConfig={[
                            {
                                getValueCallback: (row) => (
                                    <Checkbox
                                        checked={selectedProductIds.includes(row.id)}
                                        onChange={(e) => {
                                            handleProductSelection(row.id, e.target.checked);
                                        }}
                                    />
                                ),
                            },
                            {
                                name: "product_number",
                                getValueCallback: (row) => row.product_number,
                                label: t("products.columns.number"),
                                filterType: "text",
                            },
                            {
                                name: "type_id",
                                other_export_field_name: "type",
                                getValueCallback: (rowData) =>
                                    rowData.type ? getLocalizedProperty(rowData.type, "name") : "",
                                label: t("products.columns.productType"),
                                filterType: "autocomplete",
                                minWidth: 120,
                                width: 120,
                                fieldConfig: {
                                    optionLabelKey: "label",
                                    options: types || [],
                                    multiple: true,
                                    isObjectOption: true,
                                    disableClearable: false
                                },
                            },
                            {
                                name: "investment",
                                getValueCallback: (row) => row.investment.investment_name_sales,
                                label: t("products.columns.investmentName"),
                                filterType: "select",
                                fieldConfig: {
                                    options: investments || [],
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                name: "footage",
                                getValueCallback: (row) => row.footage,
                                label: t("products.columns.footage"),
                                // @ts-ignore
                                filterType: "numeric_range",
                            },
                            {
                                name: "status",
                                getValueCallback: (row) => (
                                    <ProductStatusText status={row.status} />
                                ),
                                label: t("products.columns.productStatus"),
                                filterType: "select",
                                fieldConfig: {
                                    options: productStatusesOptions || [],
                                    optionKey: "value",
                                    optionLabel: "label",
                                },
                            },
                            {
                                getValueCallback: (row) =>
                                    formatCurrency(
                                        getCalculatedPricingForProduct(row).listed.gross,
                                    ),
                                label: t("products.columns.priceGross"),
                            },
                            {
                                getValueCallback: (row) =>
                                    formatCurrency(
                                        getCalculatedPricingForProduct(row).listed.gross_per_sqm,
                                    ),
                                label: t("products.columns.priceGrossM2"),
                            },
                            {
                                name: "note",
                                getValueCallback: (row) => row.note,
                                label: t("products.columns.note"),
                                filterType: "text",
                            },
                        ]}
                    />
                </LoaderWrapper>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isLoading}>
                    {t("cancel")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                >
                    {isLoading ? t("saving") : t("save")} ({selectedProductIds.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const DocumentInfoCard = (props) => {
    const { documentData } = props;
    const { t } = useTranslation();

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t("documents.ManageDocumentProductsDialog.document_info")}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("table.docs_table.docs_nr")}:
                            </Typography>
                            <Typography variant="body1">{documentData?.docs_nr || "-"}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("table.docs_table.docs_name")}:
                            </Typography>
                            <Typography variant="body1">
                                {documentData?.docs_name || "-"}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("table.docs_table.created")}:
                            </Typography>
                            <Typography variant="body1">
                                {documentData?.created
                                    ? DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
                                          documentData.created,
                                      )
                                    : "-"}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("table.docs_table.creator")}:
                            </Typography>
                            <Typography variant="body1">{documentData?.creator || "-"}</Typography>
                        </Box>
                    </Grid>
                    {documentData?.docs_industry && (
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    {t("table.docs_table.docs_industry")}:
                                </Typography>
                                <Chip
                                    label={documentData.docs_industry}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Grid>
                    )}
                    {documentData?.docs_investant && (
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    {t("table.docs_table.standard")}:
                                </Typography>
                                <Chip
                                    label={documentData.docs_investant.docs_investan_name}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};
